import React, { FC, memo, useCallback, useEffect, useRef, useState, CSSProperties } from 'react';
import classes from './styles.module.css';
import { FaMinus, FaPlus } from "react-icons/fa6";
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useCartActionMutation } from 'src/store/api/cart/catsApiSlice';
import { isAuthSelector } from 'src/store/reducers/auth';
import { cartItemQty, cartUpdateItems } from 'src/store/reducers/cart';
import { RootState } from 'src/store/store';
import { mapServerCartItems, storageSetCartItems, storageUpdateCartItemQty } from 'src/utils/cart';
import { getCartErrorMessage } from 'src/utils/cartErrors';
import { CartItemType } from 'src/types/cart/CartItem';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface QtyInputType {
    qty: number;
    itemSlug: string;
    maxQty?: number;
    inputStyle?: string | CSSProperties;
    conStyle?: string | CSSProperties;
    inputExtraClassName?: string;
    conExtraClassName?: string;
    fluid?: boolean;
}

// Network sync of typed/clicked quantities is debounced so a burst of changes
// (1 → 2 → 3) collapses into a single latest-wins request, and a monotonic
// sequence guards against an older response landing after a newer one.
const SYNC_DEBOUNCE_MS = 450;
const CART_ERROR_TOAST_ID = "cart-action-error";

const QtyInput: FC<QtyInputType> = ({
    qty,
    itemSlug,
    maxQty = 10000,
    inputStyle,
    conStyle,
    inputExtraClassName = "",
    conExtraClassName = "",
    fluid = false
}) => {
    const { t } = useTranslation("components");
    const { t: tCart } = useTranslation("cart");
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const [inputValue, setInputValue] = useState(qty.toString());
    const [sendCartAction] = useCartActionMutation();
    const isAuth = useSelector(isAuthSelector);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const seqRef = useRef(0);
    const latestQtyRef = useRef(qty);
    const batchPrevItemsRef = useRef<CartItemType[] | null>(null);
    const mountedRef = useRef(true);

    const TOAST_ID = "qty-limit";
    const decreaseDisabled = qty <= 1;
    const increaseDisabled = qty >= maxQty;

    const showLimitToast = (message: string) => {
        if (toast.isActive(TOAST_ID)) {
            toast.update(TOAST_ID, { render: message, type: "warning", autoClose: 2000 });
        } else {
            toast.warning(message, { toastId: TOAST_ID, autoClose: 2000 });
        }
    };

    const reconcile = useCallback((serverItems: unknown) => {
        if (!store.getState().auth.access) return;
        const items = mapServerCartItems(serverItems);
        dispatch(cartUpdateItems({ items }));
        storageSetCartItems(items);
    }, [dispatch, store]);

    const flushSync = useCallback(async () => {
        timerRef.current = null;
        const seqId = ++seqRef.current;
        const qtyToSend = latestQtyRef.current;
        const prevItems = batchPrevItemsRef.current ?? store.getState().cart.items;

        try {
            const payload = await sendCartAction({ action: "update", slug: itemSlug, qty: qtyToSend }).unwrap();
            // Only the most recent request (with no newer one queued) reconciles,
            // and never after unmount — a late response for a removed line must
            // not re-add it.
            if (mountedRef.current && seqId === seqRef.current && timerRef.current === null) {
                batchPrevItemsRef.current = null;
                reconcile(payload?.items);
            }
        } catch (error) {
            if (mountedRef.current && seqId === seqRef.current && timerRef.current === null) {
                batchPrevItemsRef.current = null;
                if (store.getState().auth.access) {
                    dispatch(cartUpdateItems({ items: prevItems }));
                    storageSetCartItems(prevItems);
                }
                toast.error(getCartErrorMessage(error, tCart), { toastId: CART_ERROR_TOAST_ID });
            }
        }
    }, [dispatch, store, sendCartAction, itemSlug, reconcile, tCart]);

    const updateQty = useCallback((newQty: number) => {
        const snapshot = store.getState().cart.items;

        // Optimistic local update — instant for both guests and authed users.
        dispatch(cartItemQty({ slug: itemSlug, qty: newQty }));
        storageUpdateCartItemQty(itemSlug, newQty);

        if (!isAuth) return;

        if (batchPrevItemsRef.current === null) {
            batchPrevItemsRef.current = snapshot;
        }
        latestQtyRef.current = newQty;

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flushSync, SYNC_DEBOUNCE_MS);
    }, [store, dispatch, itemSlug, isAuth, flushSync]);

    const onChangeHandle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (!/^\d*$/.test(value)) return;

        if (value === "") {
            // Allow a transient empty field while typing; committed on blur.
            setInputValue("");
            return;
        }

        let parsedQty = parseInt(value, 10);
        if (parsedQty < 1) parsedQty = 1;

        if (parsedQty > maxQty) {
            showLimitToast(t("qty_input.error_msg", { maxQty }));
            setInputValue(maxQty.toString());
            updateQty(maxQty);
            return;
        }

        setInputValue(parsedQty.toString());
        updateQty(parsedQty);
    };

    const onBlurHandle = () => {
        const parsed = parseInt(inputValue, 10);
        if (inputValue === "" || Number.isNaN(parsed) || parsed < 1) {
            setInputValue(qty >= 1 ? qty.toString() : "1");
        }
    };

    const onIncreaseHandle = () => {
        if (increaseDisabled) {
            showLimitToast(t("qty_input.error_msg", { maxQty }));
            setInputValue(maxQty.toString());
            return;
        }
        updateQty(qty + 1);
    };

    const onDecreaseHandle = () => {
        if (decreaseDisabled) return;
        updateQty(qty - 1);
    };

    useEffect(() => {
        setInputValue(qty.toString());
    }, [qty]);

    // Cancel any pending network sync if the control unmounts (e.g. the line was
    // removed). This prevents a late update from re-adding a removed product.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const increaseLabel = t("qty_input.increase");
    const decreaseLabel = t("qty_input.decrease");
    const inputLabel = t("qty_input.value_label");

    return (
        <div
            className={`${classes.qty__con} ${conExtraClassName}`}
            style={typeof conStyle !== 'string' ? conStyle : undefined}
        >
            <div className={`${classes.input__con} ${fluid ? classes.fluid : ""}`}>
                <button
                    type="button"
                    aria-label={decreaseLabel}
                    title={decreaseLabel}
                    onClick={onDecreaseHandle}
                    disabled={decreaseDisabled}
                >
                    <FaMinus size={16} />
                </button>

                <input
                    onWheel={(e) => e.currentTarget.blur()}
                    min={1}
                    max={maxQty}
                    type='number'
                    name="qty"
                    aria-label={inputLabel}
                    value={inputValue}
                    onChange={onChangeHandle}
                    onBlur={onBlurHandle}
                    className={`${typeof inputStyle === 'string' ? inputStyle : classes.input} ${inputExtraClassName}`}
                    style={typeof inputStyle !== 'string' ? inputStyle : undefined}
                />

                <button
                    type="button"
                    aria-label={increaseLabel}
                    title={increaseLabel}
                    onClick={onIncreaseHandle}
                    disabled={increaseDisabled}
                >
                    <FaPlus size={16} />
                </button>
            </div>
        </div>
    );
};

export default memo(QtyInput);
